import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAlertPopup } from 'hooks/useAlertPopup';
import { usePopup } from 'hooks/usePopup';

import CampaignErrorPopup from './CampaignErrorPopup';
import { getUnsafeImageUrls } from './getUnsafeImageUrls';
import { isUnsafeCampaignNameError } from './isUnsafeCampaignNameError';

export const useCampaignError = () => {
  const { showPopup, hidePopup } = usePopup();
  const { showAlertPopup } = useAlertPopup();
  const { t } = useTranslation('addCampaign');
  const navigate = useNavigate();

  const handleContactSupport = () => {
    hidePopup(true);
    navigate('/messenger/support');
  };

  const showAddCampaignUnsafeNamePopup = () => {
    showAlertPopup({
      header: t('error.unsafe-name-title'),
      description: t('error.unsafe-name'),
      firstButton: { label: t('common:ok') },
      testId: 'campaign-unsafe-name-alert',
    });
  };

  const showAddCampaignUnsafePhotosPopup = () => {
    showAlertPopup({
      header: t('common:sorry'),
      description: t('error.unsafe-photos'),
      firstButton: { label: t('common:ok') },
      testId: 'campaign-unsafe-photos-alert',
    });
  };

  const showAddCampaignErrorPopup = (error?: unknown, removeUnsafePhotos?: (urls: string[]) => void) => {
    if (isUnsafeCampaignNameError(error)) {
      showAddCampaignUnsafeNamePopup();
      return;
    }

    const unsafeImageUrls = getUnsafeImageUrls(error);
    if (unsafeImageUrls.length > 0) {
      removeUnsafePhotos?.(unsafeImageUrls);
      showAddCampaignUnsafePhotosPopup();
      return;
    }

    showPopup(<CampaignErrorPopup message={t('error.unable-to-list')} onContactPress={handleContactSupport} />, {
      keepPopups: true,
    });
  };

  const showCloseCampaignErrorPopup = () => {
    showPopup(<CampaignErrorPopup message={t('error.unable-to-close')} onContactPress={handleContactSupport} />, {
      keepPopups: true,
    });
  };

  const showReopenCampaignErrorPopup = () => {
    showPopup(<CampaignErrorPopup message={t('error.unable-to-reopen')} onContactPress={handleContactSupport} />, {
      keepPopups: true,
    });
  };

  return { showAddCampaignErrorPopup, showCloseCampaignErrorPopup, showReopenCampaignErrorPopup };
};
